import { useId } from "react";
import type { NotificationStep } from "@patent-studio/api-types";
import { buttonChrome } from "@/components/ui/button";
import { Panel } from "@/components/ui/Panel";
import { useNotifications } from "@/hooks/use-notifications";
import { plural } from "@/lib/plural";
import { MaybeLink } from "../../layout/MaybeLink";
import { STEP_PLACES } from "../../notifications/cadence";

const HELD = " · held until earlier sections are reviewed";

/** One step's outstanding changes: its count, and Review on the step surfaced. */
function StepLine({
  taskId,
  step,
  pending,
  surfaced,
}: {
  taskId: string;
  step: NotificationStep;
  pending: number;
  surfaced: boolean;
}) {
  const id = useId();
  const { label, to } = STEP_PLACES[step];
  return (
    <li
      aria-labelledby={id}
      className="flex items-center gap-2.5 border-t border-warning-border py-1.5"
    >
      <b id={id} className="min-w-0 flex-1 text-detail">
        {label}
      </b>
      <span className="text-xs text-muted-foreground">
        {plural(pending, "change")}
        {!surfaced && HELD}
      </span>
      {surfaced && (
        <MaybeLink
          to={to(taskId)}
          className={buttonChrome({ variant: "secondary", size: "dense" })}
        >
          Review
        </MaybeLink>
      )}
    </li>
  );
}

/**
 * "Suggested changes are still outstanding", as v0.28 draws it above the
 * review: generating is still allowed, and each step with changes pending is
 * listed with its count. Only the surfaced step can be reviewed; the later
 * ones are held until it is (lane M decision 21).
 */
export function ChangesCard({ taskId }: { taskId: string }) {
  const id = useId();
  const { data: view } = useNotifications(taskId);
  if (view === undefined || view.steps.length === 0) return null;
  return (
    <Panel
      role="region"
      aria-labelledby={id}
      tone="warning"
      pad="snug"
      className="mt-4 flex flex-col gap-2 text-foreground"
    >
      <span className="flex items-center gap-2">
        <span
          aria-hidden
          className="size-2 flex-none rounded-full bg-warning"
        />
        <b id={id} className="text-[13.5px]">
          Suggested changes are still outstanding
        </b>
      </span>
      <p className="max-w-[78ch] text-detail leading-relaxed text-muted-foreground">
        You can still generate the draft. Outstanding changes are listed in the
        Decision record, and passages built on the affected content may be
        marked in red.
      </p>
      <ul>
        {view.steps.map(({ step, pending }) => (
          <StepLine
            key={step}
            taskId={taskId}
            step={step}
            pending={pending}
            surfaced={step === view.surfaced}
          />
        ))}
      </ul>
    </Panel>
  );
}
